class BlogPostContentTable extends HTMLElement {
  #observer = null;
  #scheduled = false;
  #table = null;
  #rte = null;

  connectedCallback() {
    this.#cleanup();

    this.#table = this.querySelector('.blog-post-content__table-of-contents__table');
    const root = this.closest('.blog-post-content');
    this.#rte = root?.querySelector('rte-formatter') ?? null;

    if (!this.#table || !this.#rte) return;

    this.#table.addEventListener('click', this.#onClick);
    this.#render();
    this.#observe();
  }

  disconnectedCallback() {
    this.#cleanup();
  }

  #cleanup() {
    this.#observer?.disconnect();
    this.#observer = null;

    if (this.#table) {
      this.#table.removeEventListener('click', this.#onClick);
    }

    this.#table = null;
    this.#rte = null;
    this.#scheduled = false;
  }

  #observe() {
    if (!this.#rte) return;

    this.#observer = new MutationObserver(() => {
      if (this.#scheduled) return;
      this.#scheduled = true;
      requestAnimationFrame(() => {
        this.#scheduled = false;
        this.#render();
      });
    });

    this.#observer.observe(this.#rte, { childList: true, subtree: true });
  }

  #render() {
    if (!this.#table || !this.#rte) return;

    const headings = Array.from(this.#rte.querySelectorAll('h2')).filter((h2) => h2.textContent?.trim());
    this.hidden = headings.length === 0;
    this.#table.textContent = '';

    const fragment = document.createDocumentFragment();

    headings.forEach((h2, index) => {
      const text = h2.textContent?.trim() ?? '';
      const slug = slugify(text) || `section-${index + 1}`;
      if (!h2.id) {
        h2.id = uniqueId(this.#rte, `article-h2-${slug}`, index);
      }

      const item = document.createElement('div');
      item.className = 'artcile-h2 artcile-content-link';
      item.textContent = text;
      item.dataset.targetId = h2.id;
      fragment.appendChild(item);
    });

    this.#table.appendChild(fragment);
  }

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const item = event.target.closest('.artcile-content-link');
    if (!(item instanceof HTMLElement)) return;

    const targetId = item.dataset.targetId;
    if (!targetId) return;

    const target = document.getElementById(targetId);
    if (!target) return;

    const rootStyles = getComputedStyle(document.documentElement);
    const headerHeightRaw = rootStyles.getPropertyValue('--header-height').trim();
    const headerHeight = Number.parseFloat(headerHeightRaw) || 0;
    const extraOffset = window.innerHeight * 0.1;
    const offset = headerHeight + extraOffset;
    const top = target.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({ top, behavior: 'smooth' });
  };
}

function slugify(value) {
  return value
    .toLowerCase()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function uniqueId(scope, base, fallbackIndex) {
  const baseId = base || `article-h2-${fallbackIndex + 1}`;
  if (!scope.querySelector(`#${CSS.escape(baseId)}`)) return baseId;

  let i = 2;
  while (scope.querySelector(`#${CSS.escape(`${baseId}-${i}`)}`)) {
    i += 1;
  }
  return `${baseId}-${i}`;
}

if (!customElements.get('blog-post-content-table')) {
  customElements.define('blog-post-content-table', BlogPostContentTable);
}
